import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";
import type {
  DuplicateCheckParams,
  DuplicateCheckResponse,
} from "@/types/DuplicateOcurrence";

// ============================================================
// Hook principal - Verificar Duplicatas
// ============================================================
export const useCheckDuplicates = () => {
  return useMutation({
    mutationKey: ["checkDuplicates"],
    mutationFn: async (
      params: DuplicateCheckParams
    ): Promise<DuplicateCheckResponse> => {
      try {
        const response = await api.post("/occurrences/check-duplicates", params);
        return response.data;
      } catch (error) {
        // RN-011: Em caso de falha, retornar como se não houvesse duplicatas
        console.error("Erro na verificação de duplicatas:", error);
        return {
          hasPotentialDuplicates: false,
          duplicates: [],
          checkDurationMs: 0,
        };
      }
    },
  });
};

// Hook para acompanhar ocorrência existente (vincular como interessado)
export const useFollowOcurrence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["followOcurrence"],
    mutationFn: async (occurrenceId: string): Promise<{ success: boolean }> => {
      const response = await api.post(`/occurrences/${occurrenceId}/follow`);
      return response.data;
    },
    onSuccess: (_, occurrenceId) => {
      queryClient.invalidateQueries({ queryKey: ["isFollowing", occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["getOcurrence", occurrenceId] });
    },
  });
};

// Hook para deixar de acompanhar ocorrência
export const useUnfollowOcurrence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["unfollowOcurrence"],
    mutationFn: async (occurrenceId: string): Promise<{ success: boolean }> => {
      const response = await api.delete(`/occurrences/${occurrenceId}/follow`);
      return response.data;
    },
    onSuccess: (_, occurrenceId) => {
      queryClient.invalidateQueries({ queryKey: ["isFollowing", occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["getOcurrence", occurrenceId] });
    },
  });
};

// Hook para verificar se usuário já está seguindo uma ocorrência
export const useIsFollowingOcurrence = (occurrenceId: string) => {
  return useQuery({
    queryKey: ["isFollowing", occurrenceId],
    queryFn: async (): Promise<{ isFollowing: boolean }> => {
      try {
        const response = await api.get(`/occurrences/${occurrenceId}/is-following`);
        return response.data;
      } catch (error) {
        console.error("Erro ao verificar se está seguindo:", error);
        return { isFollowing: false };
      }
    },
    enabled: !!occurrenceId,
  });
};
